import { TBooking } from '../../../types/api';
import { TBookingGroup, TTimesheetBooking } from './types';
import { TIMESHEET_STATUS_CODE, VALIDATION_MESSAGES } from './constants';

export const isCancelledTimesheet = (booking?: TBooking | null): boolean =>
    booking?.timesheet_status_code === TIMESHEET_STATUS_CODE.CANCELLED;

export const isBookingLockedByTimesheet = (
    booking?: TBooking | null,
    generatedBookingIds?: number[],
): boolean => {
    if (!booking || isCancelledTimesheet(booking)) {
        return false;
    }
    if (booking.id != null && generatedBookingIds?.includes(booking.id)) {
        return true;
    }
    return booking.timesheet_id != null || booking.timesheet_number != null;
};

export const isNonEmptyString = (value: string | null | undefined): value is string =>
    typeof value === 'string' && value.trim().length > 0;

export const isValidNumber = (value: number | undefined | null): value is number =>
    typeof value === 'number' && !Number.isNaN(value);

export const createGroupKey = (
    freelancerId: number,
    clientId: number,
    clientPayRate?: number,
): string =>
    `freelancer:${freelancerId}-client:${clientId}-clientpay:${clientPayRate ?? 'n/a'}`;

export const parseNumberInput = (value: string): number => {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
};

export type ValidationResult = {
    isValid: boolean;
    errors: string[];
};

export const validateTimesheetGroup = (
    group: TBookingGroup | null,
    description: string,
    agentId: number | undefined,
    agentName: string,
    agentConsultantCode: string,
    timesheetDate: string,
    personnelReference: string,
    clientReference: string,
    division: string,
    department: string,
): ValidationResult => {
    const errors: string[] = [];

    if (!group) {
        errors.push(VALIDATION_MESSAGES.NO_BOOKINGS);
        return { isValid: false, errors };
    }

    if (agentId === undefined || !isNonEmptyString(agentName)) {
        errors.push(VALIDATION_MESSAGES.MISSING_AGENT);
    }
    if (!isNonEmptyString(agentConsultantCode)) {
        errors.push(VALIDATION_MESSAGES.MISSING_CONSULTANT_CODE);
    }
    if (!isNonEmptyString(timesheetDate)) {
        errors.push(VALIDATION_MESSAGES.MISSING_TIMESHEET_DATE);
    }
    if (!isNonEmptyString(personnelReference)) {
        errors.push(VALIDATION_MESSAGES.MISSING_PERSONNEL_REF);
    }
    if (!isNonEmptyString(clientReference)) {
        errors.push(VALIDATION_MESSAGES.MISSING_CLIENT_REF);
    }
    if (!isNonEmptyString(division)) {
        errors.push(VALIDATION_MESSAGES.MISSING_DIVISION);
    }
    if (!isNonEmptyString(department)) {
        errors.push(VALIDATION_MESSAGES.MISSING_DEPARTMENT);
    }
    if (!description.trim()) {
        errors.push(VALIDATION_MESSAGES.MISSING_DESCRIPTION);
    }

    group.bookings.forEach((booking, index) => {
        const bookingLabel = booking.reference_number
            ? `Booking ${booking.reference_number}`
            : `Booking ${index + 1}`;

        if (!isValidNumber(booking.duration)) {
            errors.push(`${VALIDATION_MESSAGES.MISSING_DURATION} (${bookingLabel})`);
        }
        if (!isValidNumber(booking.freelancerPayRate)) {
            errors.push(`${VALIDATION_MESSAGES.MISSING_HOURLY_RATE} (${bookingLabel})`);
        }
        if (!isValidNumber(booking.clientPayRate)) {
            errors.push(`${VALIDATION_MESSAGES.MISSING_CLIENT_PAY} (${bookingLabel})`);
        }
        if (!isNonEmptyString(booking.rateCode)) {
            errors.push(`${VALIDATION_MESSAGES.MISSING_RATE_CODE} (${bookingLabel})`);
        }
        if (!isNonEmptyString(booking.rateDescription)) {
            errors.push(`${VALIDATION_MESSAGES.MISSING_RATE_DESCRIPTION} (${bookingLabel})`);
        }
    });

    return {
        isValid: errors.length === 0,
        errors,
    };
};

export const createNumberInputHandler = (
    bookingId: number,
    field: 'duration' | 'freelancerPayRate' | 'clientPayRate',
    updateBookingField: (
        id: number,
        updater: (booking: TTimesheetBooking) => TTimesheetBooking,
    ) => void,
) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseNumberInput(e.target.value);
    updateBookingField(bookingId, current => ({
        ...current,
        [field]: value,
    }));
};

export const createTextInputHandler = (
    bookingId: number,
    field: 'rateCode' | 'rateDescription',
    updateBookingField: (
        id: number,
        updater: (booking: TTimesheetBooking) => TTimesheetBooking,
    ) => void,
) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    updateBookingField(bookingId, current => ({
        ...current,
        [field]: e.target.value,
    }));
};

/**
 * Converts duration from hours (number) to HH:MM:SS format
 * @param hours - Duration in hours as a decimal number
 * @returns Formatted time string in HH:MM:SS format
 */
export const convertDurationToTimeFormat = (hours: number): string => {
    const totalSeconds = Math.round(hours * 3600);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};
